'use client'
import { __awaiter } from '../_virtual/_tslib.js';
import { http, serializeTransaction, erc20Abi, parseSignature } from 'viem';
import { toAccount } from 'viem/accounts';
import { hashAuthorization } from 'viem/utils';
import { EthereumWalletConnector, createWalletClientWithUiConfirmation, getOrMapViemChain, ViemUiTransaction } from '@dynamic-labs/ethereum-core';
import { Logger } from '@dynamic-labs/logger';
import { DynamicError } from '@dynamic-labs/utils';
import { withDynamicWaas } from '@dynamic-labs/waas';
import { isSameAddress } from '@dynamic-labs/wallet-connector-core';

const logger = new Logger('DynamicWaasConnector');
// This will need to be moved to a base class once we have base classes for waas connectors
class WaasExportHandler {
    constructor() {
        this.iframeStamper = null;
    }
    setIframeStamper(iframe) {
        this.iframeStamper = iframe;
    }
    clear() {
        if (this.iframeStamper) {
            this.iframeStamper.remove();
            this.iframeStamper = null;
        }
    }
}
class DynamicWaasEVMConnector extends withDynamicWaas(EthereumWalletConnector) {
    constructor(props) {
        super(props);
        this.name = 'Dynamic Waas';
        this.overrideKey = 'dynamicwaas';
        this.isEmbeddedWallet = true;
        this.__exportHandler = new WaasExportHandler();
    }
    set verifiedCredentials(verifiedCredentials) {
        this._verifiedCredentials = verifiedCredentials;
    }
    get verifiedCredentials() {
        return this._verifiedCredentials;
    }
    set verifiedCredential(verifiedCredential) {
        this._verifiedCredential = verifiedCredential;
    }
    get verifiedCredential() {
        return this._verifiedCredential;
    }
    setVerifiedCredentials(verifiedCredentials) {
        const dynamicWaasVerifiedCredentials = verifiedCredentials === null || verifiedCredentials === void 0 ? void 0 : verifiedCredentials.reduce((acc, vc) => {
            if (vc.walletName === 'dynamicwaas' && vc.chain === 'eip155') {
                const smartWallet = verifiedCredentials.find((v) => v.signerRefId === vc.id);
                const smartWalletRefId = smartWallet === null || smartWallet === void 0 ? void 0 : smartWallet.id;
                const smartWalletRefAddress = smartWallet === null || smartWallet === void 0 ? void 0 : smartWallet.address;
                acc.push(Object.assign(Object.assign({}, vc), { smartWalletRefAddress, smartWalletRefId }));
            }
            return acc;
        }, []);
        const [dynamicWaasVerifiedCredential] = dynamicWaasVerifiedCredentials;
        const didDynamicWaasVerifiedCredentialsChanged = JSON.stringify(this.verifiedCredentials) !==
            JSON.stringify(dynamicWaasVerifiedCredentials);
        if (!didDynamicWaasVerifiedCredentialsChanged) {
            return;
        }
        this.verifiedCredential = dynamicWaasVerifiedCredential;
        this.verifiedCredentials = dynamicWaasVerifiedCredentials;
    }
    setlastUsedChainId(chainId) {
        if (chainId === undefined) {
            localStorage.removeItem(DynamicWaasEVMConnector.lastUsedChainIdStorageKey);
        }
        else {
            localStorage.setItem(DynamicWaasEVMConnector.lastUsedChainIdStorageKey, chainId.toString());
        }
    }
    getlastUsedChainId() {
        const lastUsedChainIdLS = localStorage.getItem(DynamicWaasEVMConnector.lastUsedChainIdStorageKey);
        if (!lastUsedChainIdLS)
            return undefined;
        try {
            const chainId = parseInt(lastUsedChainIdLS);
            if (isNaN(chainId)) {
                return undefined;
            }
            const isChainCurrentlyEnabled = this.evmNetworks.some((network) => network.chainId === chainId);
            if (!isChainCurrentlyEnabled) {
                const lastUsedChainId = this.evmNetworks[0].chainId;
                this.setlastUsedChainId(lastUsedChainId);
                return lastUsedChainId;
            }
            return chainId;
        }
        catch (err) {
            logger.error(err);
            return undefined;
        }
    }
    currentChainId() {
        var _a, _b, _c;
        return (_a = this.getlastUsedChainId()) !== null && _a !== void 0 ? _a : (_c = (_b = this.evmNetworks) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.chainId;
    }
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.currentChainId();
        });
    }
    getEvmNetworkByChainId(chainId) {
        return this.evmNetworks.find((network) => network.chainId === chainId);
    }
    currentEvmNetwork() {
        const chainId = this.currentChainId();
        if (!chainId) {
            return undefined;
        }
        return this.getEvmNetworkByChainId(chainId);
    }
    switchNetwork(_a) {
        return __awaiter(this, arguments, void 0, function* ({ networkChainId, }) {
            if (!networkChainId) {
                return;
            }
            let networkChainIdInt = networkChainId;
            if (typeof networkChainId === 'string') {
                networkChainIdInt = parseInt(networkChainId);
            }
            this.setlastUsedChainId(networkChainIdInt);
            this.emit('chainChange', {
                chain: networkChainIdInt.toString(),
            });
        });
    }
    getRpcUrl() {
        var _a;
        const evmNetwork = this.currentEvmNetwork();
        if (!evmNetwork) {
            throw new Error('EVM network not found');
        }
        return ((_a = evmNetwork === null || evmNetwork === void 0 ? void 0 : evmNetwork.privateCustomerRpcUrls) === null || _a === void 0 ? void 0 : _a[0]) || (evmNetwork === null || evmNetwork === void 0 ? void 0 : evmNetwork.rpcUrls[0]);
    }
    validateActiveWallet(expectedAddress) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const walletClient = this.getWaasWalletClient();
            const signedSessionId = yield ((_a = this.getSignedSessionId) === null || _a === void 0 ? void 0 : _a.call(this));
            const targetWallet = yield walletClient.getWallet({
                accountAddress: expectedAddress,
                authToken: (_b = this.getAuthToken) === null || _b === void 0 ? void 0 : _b.call(this),
                signedSessionId,
            });
            if (!targetWallet) {
                throw new DynamicError('Account not found');
            }
            const isWalletActive = isSameAddress(targetWallet.accountAddress, ((_c = this.getActiveAccount()) === null || _c === void 0 ? void 0 : _c.address) || '', this.connectedChain);
            if (!isWalletActive) {
                this.setActiveAccount(targetWallet.accountAddress);
            }
        });
    }
    getViemAccount(_a) {
        return __awaiter(this, arguments, void 0, function* ({ accountAddress, }) {
            var _b;
            const client = this.getWaasWalletClient();
            const signedSessionId = yield ((_b = this.getSignedSessionId) === null || _b === void 0 ? void 0 : _b.call(this));
            return toAccount({
                address: accountAddress,
                signAuthorization: (parameters) => __awaiter(this, void 0, void 0, function* () { return this.signAuthorization(parameters); }),
                signMessage: ({ message }) => {
                    var _a;
                    return client.signMessage({
                        accountAddress,
                        authToken: (_a = this.getAuthToken) === null || _a === void 0 ? void 0 : _a.call(this),
                        message: message,
                        signedSessionId,
                    });
                },
                signTransaction: (transaction) => {
                    var _a;
                    return client
                        .signTransaction({
                        authToken: (_a = this.getAuthToken) === null || _a === void 0 ? void 0 : _a.call(this),
                        senderAddress: accountAddress,
                        signedSessionId,
                        transaction: serializeTransaction(transaction),
                    })
                        .then((tx) => tx);
                },
                signTypedData: (typedData) => client.signTypedData({
                    accountAddress,
                    signedSessionId,
                    typedData: typedData,
                }),
            });
        });
    }
    getWalletClient(chainId) {
        var _a;
        const targetAccountAddress = (_a = this.getActiveAccount()) === null || _a === void 0 ? void 0 : _a.address;
        if (!targetAccountAddress) {
            return this.getPublicClient();
        }
        const rpcUrl = this.getRpcUrl();
        const evmNetwork = chainId
            ? this.getEvmNetworkByChainId(parseInt(chainId))
            : this.currentEvmNetwork();
        if (!evmNetwork) {
            throw new Error('EVM network not found');
        }
        return createWalletClientWithUiConfirmation({
            account: () => __awaiter(this, void 0, void 0, function* () {
                return this.getViemAccount({
                    accountAddress: targetAccountAddress,
                });
            }),
            address: targetAccountAddress,
            chain: getOrMapViemChain(evmNetwork),
            transport: http(rpcUrl, this.providersConfig.httpTransportConfig),
            // TODO: remove this. We should not be passing references to wallet connectors
            walletConnector: this,
            walletUiUtils: this.walletUiUtils,
        });
    }
    createWalletAccount() {
        return __awaiter(this, arguments, void 0, function* ({ thresholdSignatureScheme = 'TWO_OF_TWO', } = {}) {
            var _a, _b;
            const walletClient = this.getWaasWalletClient();
            const signedSessionId = yield ((_a = this.getSignedSessionId) === null || _a === void 0 ? void 0 : _a.call(this));
            const createdWallet = yield walletClient.createWalletAccount({
                authToken: (_b = this.getAuthToken) === null || _b === void 0 ? void 0 : _b.call(this),
                signedSessionId,
                thresholdSignatureScheme: thresholdSignatureScheme,
            });
            return createdWallet;
        });
    }
    signMessage(message) {
        var _a;
        return (_a = this.getWalletClient()) === null || _a === void 0 ? void 0 : _a.signMessage({ message });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getWalletClient();
        });
    }
    getWalletClientByAddress({ accountAddress, }) {
        this.setActiveAccount(accountAddress);
        return this.getWalletClient();
    }
    exportPrivateKey() {
        return __awaiter(this, arguments, void 0, function* ({ accountAddress, displayContainer, password, } = {}) {
            var _a, _b;
            const walletClient = this.getWaasWalletClient();
            const targetAccountAddress = accountAddress || ((_a = this.getActiveAccount()) === null || _a === void 0 ? void 0 : _a.address);
            if (!targetAccountAddress) {
                throw new Error('Account address is required');
            }
            if (!displayContainer) {
                throw new Error('Missing display container for export private key');
            }
            this.__exportHandler.setIframeStamper(displayContainer);
            yield walletClient.exportPrivateKey({
                accountAddress: targetAccountAddress,
                authToken: (_b = this.getAuthToken) === null || _b === void 0 ? void 0 : _b.call(this),
                displayContainer,
                password,
            });
        });
    }
    getExportHandler() {
        return this.__exportHandler;
    }
    importPrivateKey(_a) {
        return __awaiter(this, arguments, void 0, function* ({ privateKey, thresholdSignatureScheme = 'TWO_OF_TWO', }) {
            var _b, _c;
            const walletClient = this.getWaasWalletClient();
            const signedSessionId = yield ((_b = this.getSignedSessionId) === null || _b === void 0 ? void 0 : _b.call(this));
            if (!signedSessionId) {
                throw new Error('Signed session ID is required');
            }
            yield walletClient.importPrivateKey({
                authToken: (_c = this.getAuthToken) === null || _c === void 0 ? void 0 : _c.call(this),
                privateKey,
                signedSessionId,
                thresholdSignatureScheme: thresholdSignatureScheme,
            });
        });
    }
    exportClientKeyshares(_a) {
        return __awaiter(this, arguments, void 0, function* ({ accountAddress, password, }) {
            var _b, _c;
            if (!accountAddress) {
                throw new Error('Account address is required');
            }
            const signedSessionId = yield ((_b = this.getSignedSessionId) === null || _b === void 0 ? void 0 : _b.call(this));
            this.setActiveAccount(accountAddress);
            const walletClient = this.getWaasWalletClient();
            yield walletClient.exportClientKeyshares({
                accountAddress,
                authToken: (_c = this.getAuthToken) === null || _c === void 0 ? void 0 : _c.call(this),
                password,
                signedSessionId,
            });
        });
    }
    backupKeySharesToGoogleDrive(_a) {
        return __awaiter(this, arguments, void 0, function* ({ accountAddress, password, }) {
            var _b;
            if (!accountAddress) {
                throw new Error('Account address is required');
            }
            this.setActiveAccount(accountAddress);
            const walletClient = this.getWaasWalletClient();
            return walletClient.backupKeySharesToGoogleDrive({
                accountAddress,
                authToken: (_b = this.getAuthToken) === null || _b === void 0 ? void 0 : _b.call(this),
                password,
            });
        });
    }
    refreshWalletAccountShares(_a) {
        return __awaiter(this, arguments, void 0, function* ({ accountAddress, password, }) {
            var _b;
            if (!accountAddress) {
                throw new Error('Account address is required');
            }
            this.setActiveAccount(accountAddress);
            const walletClient = this.getWaasWalletClient();
            return walletClient.refreshWalletAccountShares({
                accountAddress,
                authToken: (_b = this.getAuthToken) === null || _b === void 0 ? void 0 : _b.call(this),
                password,
            });
        });
    }
    updatePassword(_a) {
        return __awaiter(this, arguments, void 0, function* ({ accountAddress, existingPassword, newPassword, }) {
            var _b;
            if (!accountAddress) {
                throw new Error('Account address is required');
            }
            this.setActiveAccount(accountAddress);
            const walletClient = this.getWaasWalletClient();
            return walletClient.updatePassword({
                accountAddress,
                authToken: (_b = this.getAuthToken) === null || _b === void 0 ? void 0 : _b.call(this),
                existingPassword,
                newPassword,
            });
        });
    }
    signRawMessage(_a) {
        return __awaiter(this, arguments, void 0, function* ({ accountAddress, message, password, }) {
            var _b;
            if (!accountAddress) {
                throw new Error('Account address is required');
            }
            if (message.length !== 64) {
                throw new Error('Message must be 64 characters long');
            }
            this.setActiveAccount(accountAddress);
            const walletClient = this.getWaasWalletClient();
            return walletClient.signRawMessage({
                accountAddress,
                authToken: (_b = this.getAuthToken) === null || _b === void 0 ? void 0 : _b.call(this),
                message,
                password,
            });
        });
    }
    createUiTransaction(from) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.validateActiveWallet(from);
            const walletClient = this.getWalletClient();
            const publicClient = yield this.getPublicClient();
            if (!publicClient || !walletClient) {
                throw new DynamicError('No public client available');
            }
            return new ViemUiTransaction({
                account: from,
                onSubmit: (transaction) => __awaiter(this, void 0, void 0, function* () {
                    // Non native token
                    if (transaction.nonNativeAddress) {
                        return walletClient.writeContract({
                            abi: erc20Abi,
                            account: walletClient.account,
                            address: transaction.nonNativeAddress,
                            args: [transaction.to, transaction.nonNativeValue],
                            functionName: 'transfer',
                            maxFeePerGas: transaction.maxFeePerGas,
                            maxPriorityFeePerGas: transaction.maxPriorityFeePerGas,
                        });
                    }
                    // Native token
                    return walletClient.sendTransaction({
                        account: walletClient.account,
                        data: '0x',
                        maxFeePerGas: transaction.maxFeePerGas,
                        maxPriorityFeePerGas: transaction.maxPriorityFeePerGas,
                        to: transaction.to,
                        value: transaction.value,
                    });
                }),
                publicClient,
                transaction: {},
            });
        });
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const verifiedCredentials = this.verifiedCredentials || [];
            const addresses = verifiedCredentials
                .map((vc) => vc === null || vc === void 0 ? void 0 : vc.address)
                .filter((a) => typeof a === 'string');
            if (addresses.length > 0 && ((_a = this.verifiedCredential) === null || _a === void 0 ? void 0 : _a.address)) {
                this.setActiveAccount(this.verifiedCredential.address);
            }
            return addresses;
        });
    }
    getAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const activeAccount = this.getActiveAccount();
            if (!activeAccount || !activeAccount.address) {
                // If no active account, try to get one from verified credentials
                if ((_a = this.verifiedCredential) === null || _a === void 0 ? void 0 : _a.address) {
                    const { address } = this.verifiedCredential;
                    this.setActiveAccount(address);
                    return address;
                }
                throw new DynamicError('No active wallet address found');
            }
            return activeAccount.address;
        });
    }
    signAuthorization(parameters, password) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const { address: contractAddress, nonce, chainId } = parameters;
            if (!contractAddress) {
                throw new Error('Contract address not found');
            }
            const [signer, publicClient] = yield Promise.all([
                this.getSigner(),
                this.getPublicClient(),
            ]);
            if (!signer || !publicClient) {
                throw new Error('Required clients not found');
            }
            const { address } = signer.account;
            const walletClient = this.getWaasWalletClient();
            const signedSessionId = yield ((_a = this.getSignedSessionId) === null || _a === void 0 ? void 0 : _a.call(this));
            const signature = yield walletClient.signRawMessage({
                accountAddress: address,
                authToken: (_b = this.getAuthToken) === null || _b === void 0 ? void 0 : _b.call(this),
                message: hashAuthorization({
                    address: contractAddress,
                    chainId,
                    nonce,
                }).slice(2),
                password,
                signedSessionId,
            });
            const parsedSignature = parseSignature(signature);
            return {
                address: contractAddress,
                chainId,
                nonce,
                r: parsedSignature.r,
                s: parsedSignature.s,
                v: parsedSignature.v,
                yParity: parsedSignature.yParity,
            };
        });
    }
    isAtomicSupported(chainId) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.debug('[DynamicWaasEVMConnector] isAtomicSupported - not supported', {
                chainId,
            });
            return false;
        });
    }
    isPaymasterServiceSupported(chainId) {
        return __awaiter(this, void 0, void 0, function* () {
            logger.debug('[DynamicWaasEVMConnector] isPaymasterServiceSupported - not supported', {
                chainId,
            });
            return false;
        });
    }
    isSignAuthorizationSupported() {
        return true;
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            const waasEvmClient = this.getWaasWalletClient();
            if (!waasEvmClient) {
                return;
            }
            yield waasEvmClient.cleanup();
        });
    }
}
DynamicWaasEVMConnector.lastUsedChainIdStorageKey = 'dynamic-waas-evm-last-used-chain-id';

export { DynamicWaasEVMConnector };
